import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

// Port settings schema
const portFormSchema = z.object({
  show_ports: z.boolean(),
  favorite_port: z.string().optional(),
  favorite_port_data: z
    .object({
      id: z.number(),
      name: z.string(),
      country: z.string(),
      locode: z.string(),
      position: z.object({
        coordinates: z.array(z.number()),
      }),
    })
    .optional(),
});

export type PortFormValues = z.infer<typeof portFormSchema>;

export function usePortForm() {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const { setUnsavedSettings } = useSettingsPage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues: PortFormValues = {
    show_ports: settings?.['ribbonPlugin.settings']?.show_ports ?? true,
    favorite_port: settings?.['ribbonPlugin.settings']?.favorite_port ?? '',
    favorite_port_data: settings?.['ribbonPlugin.settings']?.favorite_port_data,
  };

  const form = useForm<PortFormValues>({
    resolver: zodResolver(portFormSchema),
    defaultValues: initialValues,
  });

  // Watch for form changes to track unsaved state
  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });
    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  const onSubmit = (data: PortFormValues) => {
    setIsSubmitting(true);

    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        show_ports: data.show_ports,
        favorite_port: data.favorite_port,
        favorite_port_data: data.favorite_port_data,
      },
    };

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          title: messages.genericToastTitleSuccess,
          description: 'Port settings have been saved successfully.',
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          title: messages.genericToastTitleError,
          description: 'Failed to save port settings. Please try again.',
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    initialValues,
  };
}
